import { useEffect, useRef } from 'react';
import { useSignal, viewport } from '@telegram-apps/sdk-react';

export default function SafeAreaBinder({
  debug = false,
  alertOnce = false,
  disableWhile = false,
  iosFallbackTop = 96,
  androidFallbackTop = 76,
}) {
  const safeArea = useSignal(viewport.safeAreaInsets);
  const contentSafeArea = useSignal(viewport.contentSafeAreaInsets);

  const alerted = useRef(false);

  const num = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const toInsets = (i) => {
    if (!i) return null;
    const t = num(i.top), b = num(i.bottom), l = num(i.left), r = num(i.right);
    return (t || b || l || r) ? { t, b, l, r } : null;
  };

  const setCSS = ({ t = 0, b = 0, l = 0, r = 0 }) => {
    const css = document.documentElement.style;
    css.setProperty('--tg-safe-top', `${t}px`);
    css.setProperty('--tg-safe-bottom', `${b}px`);
    css.setProperty('--tg-safe-left', `${l}px`);
    css.setProperty('--tg-safe-right', `${r}px`);
  };

  // State from useSignal
  const s = toInsets(safeArea.value);
  const c = toInsets(contentSafeArea.value);

  // Use a single effect to handle both initial fallbacks and SDK updates
  useEffect(() => {
    if (disableWhile) {
      return;
    }

    const tg = window?.Telegram?.WebApp;
    if (!tg) {
      return;
    }

    const handleUpdate = () => {
      const sdkSafe = toInsets(viewport.safeAreaInsets.value);
      const sdkContent = toInsets(viewport.contentSafeAreaInsets.value);
      const best = sdkSafe || sdkContent;

      if (best) {
        setCSS(best);
        if (debug) console.log('[safe-area:sdk]', best);
        if (alertOnce && !alerted.current) {
          alerted.current = true;
          alert(`[sdk] ${JSON.stringify(best)}`);
        }
      }
    };

    // 1. Initial check (fallback)
    const initialSafe = toInsets(tg.viewport?.safeAreaInsets || tg.safeAreaInsets);
    const initialContent = toInsets(tg.viewport?.contentSafeAreaInsets || tg.contentSafeAreaInsets);
    const initialBest = initialSafe || initialContent;

    if (initialBest) {
      setCSS(initialBest);
      if (debug) console.log('[safe-area:fallback:telegram]', initialBest);
    } else {
      const platform = (tg.platform || '').toLowerCase();
      if (platform === 'ios') {
        const gcs = getComputedStyle(document.documentElement);
        const env = toInsets({
          top: gcs.getPropertyValue('env(safe-area-inset-top)'),
          bottom: gcs.getPropertyValue('env(safe-area-inset-bottom)'),
          left: gcs.getPropertyValue('env(safe-area-inset-left)'),
          right: gcs.getPropertyValue('env(safe-area-inset-right)'),
        });
        if (env) {
          setCSS(env);
          if (debug) console.log('[safe-area:fallback:ios-env]', env);
        } else {
          setCSS({ t: iosFallbackTop, b: 0, l: 0, r: 0 });
          if (debug) console.log('[safe-area:fallback:ios-hard]', { t: iosFallbackTop });
        }
      } else if (platform === 'android') {
        setCSS({ t: androidFallbackTop, b: 0, l: 0, r: 0 });
        if (debug) console.log('[safe-area:fallback:android-hard]', { t: androidFallbackTop });
      } else {
        setCSS({ t: 0, b: 0, l: 0, r: 0 });
        if (debug) console.log('[safe-area:fallback:zero]');
      }
    }

    // 2. Listen for changes
    tg.onEvent('viewportChanged', handleUpdate);

    // 3. Cleanup on unmount
    return () => {
      tg.offEvent('viewportChanged', handleUpdate);
    };

  }, [disableWhile, debug, alertOnce, iosFallbackTop, androidFallbackTop]);

  if (!debug) {
    return null;
  }

  const read = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '0px';

  return (
    <div style={{ position: 'fixed', bottom: 8, right: 8, zIndex: 999999, background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '6px 8px', borderRadius: 8, fontSize: 12, fontFamily: 'monospace', pointerEvents: 'none' }}>
      <div>SDK safe: {JSON.stringify(s || {})}</div>
      <div>SDK content: {JSON.stringify(c || {})}</div>
      <div>CSS: t{read('--tg-safe-top')} / b{read('--tg-safe-bottom')} / l{read('--tg-safe-left')} / r{read('--tg-safe-right')}</div>
    </div>
  );
}